import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import { useColors } from '../../theme/ColorModeContext';
import { DIFFICULTY_LABELS, DIFFICULTY_ORDER, formatClock, gradeFor, QUIZ_DURATION_MIN, QuizDifficulty } from './quizEngine';

export type TierScores = Record<QuizDifficulty, { correct: number; total: number }>;

interface ResultSummaryProps {
    correct: number;
    total: number;
    perTier: TierScores;
    elapsedMs: number;
    timedOut: boolean;
}

/** Score, grade, per-difficulty breakdown and time used, shared by the results screen and the public result page. */
const ResultSummary = ({ correct, total, perTier, elapsedMs, timedOut }: ResultSummaryProps) => {
    const colors = useColors();
    const diffColor: Record<QuizDifficulty, string> = {
        easy: colors.difficulty.easy,
        medium: colors.difficulty.medium,
        hard: colors.difficulty.veryHard,
    };
    const pct = total ? Math.round((correct / total) * 100) : 0;

    return (
        <>
            <Typography
                sx={{
                    fontFamily: '"Orbitron", sans-serif',
                    fontWeight: 800,
                    fontSize: { xs: '2.8rem', md: '3.6rem' },
                    color: colors.text.primary,
                    lineHeight: 1,
                }}
            >
                {correct}
                <Box component="span" sx={{ color: colors.text.muted, fontSize: '0.5em' }}>
                    {' '}
                    / {total}
                </Box>
            </Typography>
            <Typography
                sx={{
                    fontFamily: '"Share Tech Mono", monospace',
                    color: colors.accent.cyan,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    mt: 1,
                }}
            >
                {pct}% &middot; {gradeFor(pct)}
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: '12px',
                    mt: 3,
                }}
            >
                {DIFFICULTY_ORDER.map((d) => (
                    <Box
                        key={d}
                        sx={{
                            p: '12px 14px',
                            borderRadius: '8px',
                            border: `1px solid ${colors.border.subtle}`,
                            backgroundColor: colors.bg.tertiary,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: '"Share Tech Mono", monospace',
                                fontSize: '0.7rem',
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                color: diffColor[d],
                            }}
                        >
                            {DIFFICULTY_LABELS[d]}
                        </Typography>
                        <Typography sx={{ color: colors.text.primary, fontWeight: 700 }}>
                            {perTier[d].correct} / {perTier[d].total}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    mt: 3,
                    color: timedOut ? colors.accent.red : colors.text.secondary,
                }}
            >
                <AccessTimeRoundedIcon sx={{ fontSize: '1rem' }} />
                <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.82rem' }}>
                    {timedOut
                        ? `Time's up: auto-submitted after ${formatClock(elapsedMs)}`
                        : `Time used: ${formatClock(elapsedMs)} of ${QUIZ_DURATION_MIN}:00`}
                </Typography>
            </Box>
        </>
    );
};

export default ResultSummary;
